import deepEqual from 'fast-deep-equal';
import React, { useMemo, createContext, useContext, useReducer } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as yup from 'yup';

import { logger } from 'src/utils/datadog-log-wrapper';

import { trackPayablesFiltersChanged } from '../analytics';

export type FiltersState = {
  search: string | undefined;
};

const filtersStateSchema = yup.object().shape({
  search: yup.string().trim().default(undefined),
});

const defaultState: FiltersState = {
  search: undefined,
};

type FilterReducerAction =
  | { type: 'SET_SEARCH_FILTER'; search: string }
  | { type: 'RESET_FILTERS' }
  | { type: 'SET_FILTERS_FROM_URL'; state: FiltersState };

export type FilterActions = ReturnType<typeof actionsFactory>;

const reducer = (
  state: FiltersState,
  action: FilterReducerAction,
): FiltersState => {
  switch (action.type) {
    case 'SET_SEARCH_FILTER': {
      return { ...state, search: action.search };
    }
    case 'RESET_FILTERS': {
      return defaultState;
    }
    case 'SET_FILTERS_FROM_URL': {
      return { ...state, ...action.state };
    }
    default:
      throw new Error(`Unexpected action ${action}`);
  }
};

const actionsFactory = (dispatch: (action: FilterReducerAction) => void) => ({
  setSearch: (search: string) => {
    trackPayablesFiltersChanged('SET_SEARCH_FILTER');
    dispatch({ type: 'SET_SEARCH_FILTER', search });
  },
  reset: () => {
    trackPayablesFiltersChanged('RESET_FILTERS');
    dispatch({ type: 'RESET_FILTERS' });
  },
});

const FiltersContext: React.Context<{
  state: FiltersState;
  actions: FilterActions;
  updateUrlParams: (filters: FiltersState) => void;
}> = createContext({
  state: defaultState,
  actions: actionsFactory(() => {}),
  updateUrlParams: (_) => {},
});

const getRawFiltersFromURLSearch = (search: string) => {
  const urlParams = new URLSearchParams(search);

  return {
    search: urlParams.get('search') ?? undefined,
  };
};

const getFilterStateFromUrlSearch = (
  search: string,
  state: FiltersState = defaultState,
): FiltersState | undefined => {
  if (search) {
    const rawFilters = getRawFiltersFromURLSearch(search);

    if (!deepEqual(rawFilters, state)) {
      try {
        return {
          ...defaultState,
          ...filtersStateSchema.validateSync(rawFilters),
        };
      } catch (error) {
        logger.warn('Unable to safely parse url params on all payables', {
          error,
          search,
          team: 'finance-accountant',
          scope: '',
        });
      }
    }
  }
};

export const FiltersContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const history = useHistory();

  const [state, dispatch] = useReducer(
    reducer,
    getFilterStateFromUrlSearch(location.search) || defaultState,
  );

  const actions = useMemo(() => actionsFactory(dispatch), []);

  const updateUrlParams = (filters: FiltersState) => {
    const searchParams = new URLSearchParams();

    if (filters.search) {
      searchParams.append('search', filters.search);
    }

    history.push(`${location.pathname}?${searchParams.toString()}`);
  };

  // useEffect(() => {
  //   const newFilterState = getFilterStateFromUrlSearch(location.search, state);
  //   if (newFilterState) {
  //     actions.setFiltersFromUrl(newFilterState);
  //   }
  // }, [location.search]);

  const contextValue = { state, actions, updateUrlParams };

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
};

export const useFiltersContext = () => useContext(FiltersContext);
